import { VariantProps } from 'class-variance-authority';
import { Command as CommandPrimitive } from 'cmdk';
import { Slot } from '@radix-ui/react-slot';
import { useMemo } from 'react';
import * as React from 'react';

import { CommandEmpty, CommandGroup, CommandItem, CommandList, Command } from '../command/command';
import { selectFloatingPlaceholderVariants, selectTriggerVariants } from '../select/select';
import { PopoverContent, PopoverTrigger, Popover } from '../popover/popover';
import { typographyVariants, Typography } from '../typography/typography';
import { ArrowDown, Search } from '../icons';
import { Input } from '../input/input';
import { cn } from '../../utils';

type ItemElement = React.ReactElement<
  React.ComponentProps<typeof ComboboxItem>,
  typeof ComboboxItem
>;
interface ComboboxContextValue {
  selected?: string;
  items: ItemElement[];
  setSelected: (value?: ComboboxContextValue['selected']) => void;
}

const ComboboxContext = React.createContext<ComboboxContextValue>({
  items: [],
  setSelected: () => {},
});

const useCombobox = () => {
  const context = React.useContext(ComboboxContext);
  if (!context) {
    throw new Error('useCombobox must be used inside ComboboxProvider');
  }

  return context;
};

const defaultSearch = (search: string) => (keyword: string) =>
  keyword.toLocaleLowerCase().includes(search.toLocaleLowerCase());

const flattenChildren = (node: React.ReactNode): React.ReactElement[] => {
  if (Array.isArray(node)) return React.Children.toArray(node).flatMap(flattenChildren);
  if (!React.isValidElement(node)) return [];

  const props = node.props as React.PropsWithChildren;

  if (!props.children) return [node];

  return props.children
    ? [node, ...React.Children.toArray(props.children).flatMap(flattenChildren)]
    : [node];
};

const filterItemElements = (children: React.ReactNode): ItemElement[] => {
  return flattenChildren(children).filter((child) => child.type === ComboboxItem) as ItemElement[];
};

export interface ComboboxProps extends React.ComponentPropsWithoutRef<typeof Command> {
  defaultValue?: string;
  selected?: string;
  onSelectedChange?: (value?: string) => void;
}

const Combobox = React.forwardRef<React.ElementRef<typeof Command>, ComboboxProps>(
  (
    { selected: value, className, children, filter, onSelectedChange = () => {}, ...props },
    ref,
  ) => {
    const [selected, setSelected] = React.useState<ComboboxContextValue['selected']>(value);
    const [open, setOpen] = React.useState(false);

    const filterFn = React.useCallback(
      (value: string, search: string, keywords?: string[]) => {
        if (filter) return filter(value, search, keywords);

        return keywords?.some(defaultSearch(search)) ? 1 : 0;
      },
      [filter],
    );

    const handleSelect = React.useCallback(
      (value: ComboboxContextValue['selected']) => {
        setSelected(value);
        setOpen(false);
        onSelectedChange(value);
      },
      [setSelected, setOpen, onSelectedChange],
    );

    const items = useMemo(() => filterItemElements(children), [children]);

    React.useEffect(() => {
      setSelected(value);
    }, [value]);

    return (
      <ComboboxContext.Provider value={{ selected, setSelected: handleSelect, items }}>
        <Command
          className={cn('overflow-visible bg-transparent', className)}
          shouldFilter={open}
          filter={filterFn}
          ref={ref}
          {...props}
        >
          <Popover onOpenChange={setOpen} open={open}>
            {children}
          </Popover>
        </Command>
      </ComboboxContext.Provider>
    );
  },
);
Combobox.displayName = 'Combobox';

const ComboboxValue = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { items, selected } = useCombobox();

    const selectedItem = useMemo(() => {
      const item = items.find((i) => i.props.value === selected);
      return item && React.cloneElement(item, item.props, item.props.children);
    }, [items, selected]);

    const displayValue = useMemo(() => {
      return selectedItem
        ? selectedItem.props.children || selectedItem.props.label || selectedItem.props.value
        : selected;
    }, [selectedItem, selected]);

    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          'flex items-center gap-2 text-default',
          typographyVariants({ variant: 'labelMediumBook' }),
          className,
        )}
      >
        {displayValue}
      </div>
    );
  },
);
ComboboxValue.displayName = 'ComboboxValue';

const ComboboxTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverTrigger>,
  React.ComponentPropsWithoutRef<typeof PopoverTrigger> & VariantProps<typeof selectTriggerVariants>
>(({ className, children, hasShadow, size, hasError, required, asChild, ...props }, ref) => {
  const { selected } = useCombobox();

  return (
    <PopoverTrigger data-placeholder={!selected} asChild={asChild} ref={ref} {...props}>
      {asChild ? (
        <Slot>{children}</Slot>
      ) : (
        <div
          className={cn(
            'min-w-57.5 group/state',
            selectTriggerVariants({ hasShadow, size, hasError, required }),
            className,
          )}
        >
          {children}
          <div>
            <ArrowDown className="size-4 opacity-50 text- group-data-[state=open]/state:rotate-180" />
          </div>
        </div>
      )}
    </PopoverTrigger>
  );
});
ComboboxTrigger.displayName = 'ComboboxTrigger';

const ComboboxContent = React.forwardRef<
  React.ElementRef<typeof PopoverContent>,
  React.ComponentPropsWithoutRef<typeof PopoverContent>
>(({ className, ...props }, ref) => (
  <PopoverContent
    className={cn(
      `relative touch-auto overscroll-y-contain z-100 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-border-default h-full p-0 w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)]
          bg-background shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-
          data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
      className,
    )}
    role="listbox"
    ref={ref}
    {...props}
  />
));
ComboboxContent.displayName = 'ComboboxContent';

const ComboboxInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  Omit<React.ComponentPropsWithoutRef<typeof Input>, 'name'> & { removeIcon?: boolean }
>(({ removeIcon, ...props }, ref) => (
  <div className="p-1 sticky top-0 bg-background z-10">
    <CommandPrimitive.Input ref={ref} asChild>
      <Input
        trailingIcon={!removeIcon ? { icon: <Search />, position: 'left' } : undefined}
        name="comboboxSearchInput"
        inputSize="small"
        {...props}
      />
    </CommandPrimitive.Input>
  </div>
));
ComboboxInput.displayName = 'ComboboxInput';

const ComboboxList = React.forwardRef<
  React.ElementRef<typeof CommandList>,
  React.ComponentPropsWithoutRef<typeof CommandList>
>(({ className, ...props }, ref) => (
  <CommandList
    className={cn(
      'relative mr-1 h-full max-h-full [&::-webkit-scrollbar-track]:!bg-transparent [&::-webkit-scrollbar-thumb]:!bg-black/20 [&::-webkit-scrollbar-thumb]:!rounded',
      className,
    )}
    onWheel={(event) => event.stopPropagation()}
    ref={ref}
    {...props}
  />
));
ComboboxList.displayName = 'ComboboxList';

const ComboboxEmpty = CommandEmpty;
ComboboxEmpty.displayName = 'ComboboxEmpty';

const ComboboxGroup = CommandGroup;
ComboboxGroup.displayName = 'ComboboxGroup';

const ComboboxItem = React.forwardRef<
  React.ElementRef<typeof CommandItem>,
  React.ComponentPropsWithoutRef<typeof CommandItem> & { label: string }
>(({ keywords, children, label, value, ...props }, ref) => {
  const { selected, setSelected } = useCombobox();

  const searchKeywords = React.useMemo(() => {
    if (keywords) return keywords;

    return [label];
  }, [keywords, label]);

  return (
    <CommandItem
      className={cn(
        'relative h-11 flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-3 pr-2 text-sm outline-none focus:bg-surface-hover data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        {
          'bg-surface-weak data-[selected=true]:bg-surface-weak': selected === value,
        },
        typographyVariants({ variant: 'labelMediumBook' }),
      )}
      onSelect={() => value && setSelected(value)}
      keywords={searchKeywords}
      value={value}
      ref={ref}
      {...props}
    >
      {children || label}
    </CommandItem>
  );
});
ComboboxItem.displayName = 'ComboboxItem';

interface ComboboxPlaceholderProps {
  children: React.ReactNode;
  placeholder?: string;
  floating?: boolean;
  required?: boolean;
}

const ComboboxPlaceholder = ({
  required = false,
  floating = false,
  placeholder,
  children,
}: ComboboxPlaceholderProps) => {
  const { selected } = useCombobox();
  const hasValue = useMemo(() => !!selected, [selected]);

  return (
    <div className="text-left">
      {(floating || !hasValue) && (
        <Typography
          className={cn('text-nowrap', selectFloatingPlaceholderVariants({ required, hasValue }))}
          variant="labelMediumBook"
        >
          {placeholder}
        </Typography>
      )}
      {hasValue && children}
    </div>
  );
};

export {
  ComboboxPlaceholder,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxValue,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxList,
  ComboboxItem,
  Combobox,
};
